import React, { useEffect, useMemo, useState, useCallback } from "react";
import Topbar from "../components/Topbar";
import ConfirmDialog from "../components/ConfirmDialog";
import { Checkbox } from "../components/ui/checkbox";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Wallet, Loader2, ReceiptText, ChevronDown, ChevronRight, CheckCircle2, Undo2,
} from "lucide-react";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const monthLabel = (ym) => {
  const [y, m] = (ym || "").split("-");
  const idx = parseInt(m, 10) - 1;
  if (isNaN(idx) || !MONTHS[idx]) return ym;
  return `${MONTHS[idx][0].toUpperCase()}${MONTHS[idx].slice(1)} ${y}`;
};
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("it-IT"); } catch { return s; } };

export default function AdminCommissions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyUnsettled, setOnlyUnsettled] = useState(false);
  const [collapsed, setCollapsed] = useState({});     // formatore_id -> true se chiuso
  const [selected, setSelected] = useState(new Set()); // commission id (solo righe da liquidare)
  const [confirmSettle, setConfirmSettle] = useState(false);
  const [confirmUnsettle, setConfirmUnsettle] = useState(null); // riga da riaprire
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = onlyUnsettled ? { settled: false } : {};
      const { data } = await api.get("/admin/commissions", { params });
      setData(data);
      // scarta dalla selezione gli id non più presenti/liquidati nel frattempo
      setSelected((prev) => {
        const live = new Set();
        (data.formatori || []).forEach((f) => f.months.forEach((m) => m.rows.forEach((r) => {
          if (!r.settled && prev.has(r.id)) live.add(r.id);
        })));
        return live;
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [onlyUnsettled]);

  useEffect(() => { load(); }, [load]);

  const rate = data ? Math.round(data.rate * 100) : 30;

  // righe da liquidare per formatore (id) — per la selezione "tutte"
  const unsettledIdsByFormatore = useMemo(() => {
    const map = {};
    (data?.formatori || []).forEach((f) => {
      const ids = [];
      f.months.forEach((m) => m.rows.forEach((r) => { if (!r.settled) ids.push(r.id); }));
      map[f.formatore_id] = ids;
    });
    return map;
  }, [data]);

  const allRowsById = useMemo(() => {
    const map = {};
    (data?.formatori || []).forEach((f) => f.months.forEach((m) => m.rows.forEach((r) => { map[r.id] = r; })));
    return map;
  }, [data]);

  const selectedAmount = useMemo(() => {
    let s = 0;
    selected.forEach((id) => { s += allRowsById[id]?.commission_amount || 0; });
    return Math.round(s * 100) / 100;
  }, [selected, allRowsById]);

  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleFormatore = (fid) => setSelected((prev) => {
    const ids = unsettledIdsByFormatore[fid] || [];
    const allSel = ids.length > 0 && ids.every((id) => prev.has(id));
    const next = new Set(prev);
    ids.forEach((id) => (allSel ? next.delete(id) : next.add(id)));
    return next;
  });

  const doSettle = async () => {
    setBusy(true);
    try {
      const ids = [...selected];
      const { data: res } = await api.post("/admin/commissions/settle", { commission_ids: ids });
      toast.success(`${res.updated} provvigion${res.updated === 1 ? "e" : "i"} liquidat${res.updated === 1 ? "a" : "e"}.`);
      setConfirmSettle(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doUnsettle = async (row) => {
    setBusy(true);
    try {
      await api.post("/admin/commissions/unsettle", { commission_ids: [row.id] });
      toast.success("Liquidazione annullata.");
      setConfirmUnsettle(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const isEmpty = !data || (data.formatori || []).length === 0;

  return (
    <div data-testid="admin-commissions" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
            <Wallet size={14} /> Pannello Super Admin
          </div>
          <h1 className="text-2xl sm:text-3xl text-brand-ink mb-1">Liquidazione provvigioni</h1>
          <p className="text-sm text-brand-muted">Provvigioni maturate dai formatori sui corsi venduti · aliquota {rate}%</p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-brand-muted py-12 justify-center">
            <Loader2 className="animate-spin" size={18} /> Caricamento…
          </div>
        ) : isEmpty ? (
          <div className="bg-white border border-brand-line rounded-sm p-10 text-center text-brand-muted text-sm">
            <ReceiptText size={22} className="inline mb-2 opacity-60" />
            <div>Nessuna provvigione registrata.</div>
            <div className="text-xs mt-1">
              Le provvigioni compaiono quando viene pagato un ordine che include il corso di un formatore.
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              <Metric label="Totale maturato" value={eur(data.total_commission)} accent="green" />
              <Metric label="Da liquidare" value={eur(data.total_unsettled)} accent="gold" />
              <Metric label="Già liquidato" value={eur(data.total_settled)} />
            </div>

            <div className="flex items-center justify-between mb-4">
              <label className="inline-flex items-center gap-2 text-sm text-brand-muted cursor-pointer select-none">
                <Checkbox
                  checked={onlyUnsettled}
                  onCheckedChange={(v) => setOnlyUnsettled(!!v)}
                  data-testid="filter-unsettled"
                />
                Mostra solo da liquidare
              </label>
              <span className="text-xs text-brand-muted">{data.count} provvigioni · {data.formatori.length} formatori</span>
            </div>

            <div className="space-y-4">
              {data.formatori.map((f) => {
                const unIds = unsettledIdsByFormatore[f.formatore_id] || [];
                const selCount = unIds.filter((id) => selected.has(id)).length;
                const allSel = unIds.length > 0 && selCount === unIds.length;
                const open = !collapsed[f.formatore_id];
                return (
                  <div key={f.formatore_id} className="bg-white border border-brand-line rounded-sm overflow-hidden">
                    {/* Header formatore */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-brand-bg border-b border-brand-line">
                      <button
                        onClick={() => setCollapsed((c) => ({ ...c, [f.formatore_id]: open }))}
                        className="text-brand-muted hover:text-brand-blue"
                        aria-label={open ? "Comprimi" : "Espandi"}
                      >
                        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-brand-ink truncate">{f.formatore_name || f.formatore_email || "Formatore"}</div>
                        {f.formatore_name && f.formatore_email && (
                          <div className="text-xs text-brand-muted truncate">{f.formatore_email}</div>
                        )}
                      </div>
                      <div className="hidden sm:flex items-center gap-4 text-sm">
                        <span className="text-brand-muted">Maturato <b className="text-brand-ink">{eur(f.total_commission)}</b></span>
                        <span className="text-amber-700">Da liquidare <b>{eur(f.total_unsettled)}</b></span>
                      </div>
                      {unIds.length > 0 && (
                        <button
                          onClick={() => toggleFormatore(f.formatore_id)}
                          data-testid={`select-formatore-${f.formatore_id}`}
                          className="text-xs font-semibold text-brand-blue hover:underline whitespace-nowrap"
                        >
                          {allSel ? "Deseleziona" : `Seleziona da liquidare (${unIds.length})`}
                        </button>
                      )}
                    </div>

                    {/* Mesi + righe */}
                    {open && f.months.map((m) => (
                      <div key={m.month}>
                        <div className="flex items-center justify-between px-4 py-2 border-b border-brand-line/70">
                          <div className="text-sm font-semibold text-brand-ink">{monthLabel(m.month)}</div>
                          <div className="text-xs text-brand-muted">{m.count} vendit{m.count === 1 ? "a" : "e"} · {eur(m.total_commission)}</div>
                        </div>
                        <table className="w-full text-sm">
                          <thead className="text-brand-muted">
                            <tr className="border-b border-brand-line">
                              <th className="w-8 px-2 py-2"></th>
                              <th className="text-left font-semibold px-3 py-2">Corso</th>
                              <th className="text-left font-semibold px-3 py-2">Acquirente</th>
                              <th className="text-left font-semibold px-3 py-2">Pagamento</th>
                              <th className="text-right font-semibold px-3 py-2">Incassato</th>
                              <th className="text-right font-semibold px-3 py-2">Provvigione</th>
                              <th className="text-right font-semibold px-3 py-2">Stato</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-brand-line">
                            {m.rows.map((r) => (
                              <tr key={r.id} className={selected.has(r.id) ? "bg-brand-blue/5" : ""}>
                                <td className="px-2 py-2 align-middle">
                                  {!r.settled && (
                                    <Checkbox
                                      checked={selected.has(r.id)}
                                      onCheckedChange={() => toggleRow(r.id)}
                                      data-testid={`row-${r.id}`}
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2 text-brand-ink">{r.course_title || "—"}</td>
                                <td className="px-3 py-2 text-brand-muted">{r.buyer || "—"}</td>
                                <td className="px-3 py-2 text-brand-muted">{fmtDate(r.paid_at)}</td>
                                <td className="px-3 py-2 text-right text-brand-muted">{eur(r.gross_amount)}</td>
                                <td className="px-3 py-2 text-right font-medium text-brand-ink">{eur(r.commission_amount)}</td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                  {r.settled ? (
                                    <span className="inline-flex items-center gap-2">
                                      <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700" title={r.settled_at ? `Liquidata il ${fmtDate(r.settled_at)}` : undefined}>
                                        Liquidata
                                      </span>
                                      <button
                                        onClick={() => setConfirmUnsettle(r)}
                                        className="text-brand-muted hover:text-red-600"
                                        title="Annulla liquidazione"
                                        data-testid={`unsettle-${r.id}`}
                                      >
                                        <Undo2 size={14} />
                                      </button>
                                    </span>
                                  ) : (
                                    <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700">Da liquidare</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-brand-muted mt-6">
              Segnare "liquidata" registra che la provvigione è stata pagata al formatore (es. dopo il bonifico).
              Importi e aliquota restano la foto scattata al pagamento dell'ordine e non sono modificabili.
            </p>
          </>
        )}
      </main>

      {/* Barra azioni selezione (sticky in basso) */}
      {selected.size > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-30 border-t border-brand-line bg-white/95 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
            <div className="text-sm text-brand-ink">
              <b>{selected.size}</b> selezionat{selected.size === 1 ? "a" : "e"} · <b>{eur(selectedAmount)}</b>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelected(new Set())}
                className="text-sm text-brand-muted hover:text-brand-ink px-3 py-2"
              >
                Annulla
              </button>
              <button
                onClick={() => setConfirmSettle(true)}
                data-testid="settle-selected"
                className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white text-sm font-semibold rounded-sm px-4 py-2"
              >
                <CheckCircle2 size={16} /> Segna liquidate
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmSettle}
        onOpenChange={(v) => !busy && setConfirmSettle(v)}
        title="Segnare come liquidate?"
        description={`Stai per segnare ${selected.size} provvigione/i (${eur(selectedAmount)}) come pagate al formatore. L'operazione è reversibile dalla singola riga.`}
        confirmLabel={busy ? "Attendere…" : "Segna liquidate"}
        onConfirm={doSettle}
        testid="confirm-settle"
      />

      <ConfirmDialog
        open={!!confirmUnsettle}
        onOpenChange={(v) => !busy && !v && setConfirmUnsettle(null)}
        title="Annullare la liquidazione?"
        description={confirmUnsettle ? `La provvigione "${confirmUnsettle.course_title || confirmUnsettle.course_id}" (${eur(confirmUnsettle.commission_amount)}) tornerà "da liquidare".` : ""}
        confirmLabel={busy ? "Attendere…" : "Annulla liquidazione"}
        destructive
        onConfirm={() => confirmUnsettle && doUnsettle(confirmUnsettle)}
        testid="confirm-unsettle"
      />
    </div>
  );
}

function Metric({ label, value, accent }) {
  const color = accent === "green" ? "text-emerald-700" : accent === "gold" ? "text-brand-gold-deep" : "text-brand-ink";
  return (
    <div className="bg-white border border-brand-line rounded-sm px-4 py-3">
      <div className="text-xs text-brand-muted">{label}</div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}
